import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../../components/AppIcon';

const STATUS = {
  CHECKING: 'checking',
  OK: 'ok',
  ERROR: 'error',
  WARNING: 'warning',
  IDLE: 'idle',
};

const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;

function diagnoseConfig() {
  const issues = [];
  const info = [];

  if (!supabaseUrl || supabaseUrl === 'https://placeholder.supabase.co') {
    issues?.push({ type: 'error', msg: 'VITE_SUPABASE_URL is missing or placeholder' });
  } else if (!supabaseUrl?.startsWith('https://')) {
    issues?.push({ type: 'error', msg: 'VITE_SUPABASE_URL must start with https://' });
  } else if (!supabaseUrl?.includes('.supabase.co')) {
    issues?.push({ type: 'warning', msg: 'VITE_SUPABASE_URL does not look like a standard Supabase URL' });
  } else {
    info?.push({ label: 'Project URL', value: supabaseUrl });
  }

  if (!supabaseAnonKey || supabaseAnonKey === 'placeholder-key') {
    issues?.push({ type: 'error', msg: 'VITE_SUPABASE_ANON_KEY is missing or placeholder' });
  } else if (supabaseAnonKey?.length < 100) {
    issues?.push({ type: 'warning', msg: 'Anon key looks too short — may be invalid' });
  } else {
    const masked = supabaseAnonKey?.slice(0, 12) + '...' + supabaseAnonKey?.slice(-6);
    info?.push({ label: 'Anon Key', value: masked });
  }

  return { issues, info };
}

async function testRestEndpoint() {
  if (!supabaseUrl || supabaseUrl === 'https://placeholder.supabase.co') {
    return { ok: false, latency: null, error: 'No valid URL configured' };
  }
  const start = Date.now();
  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller?.abort(), 6000);
    const res = await fetch(`${supabaseUrl}/rest/v1/`, {
      method: 'GET',
      headers: { apikey: supabaseAnonKey || '', 'Content-Type': 'application/json' },
      signal: controller?.signal,
    });
    clearTimeout(timeout);
    const latency = Date.now() - start;
    const reachable = [200, 400, 401, 404]?.includes(res?.status);
    return { ok: reachable, latency, httpStatus: res?.status, error: reachable ? null : `HTTP ${res?.status}` };
  } catch (err) {
    const latency = Date.now() - start;
    if (err?.name === 'AbortError') return { ok: false, latency, error: 'Request timed out (>6s)' };
    return { ok: false, latency, error: err?.message || 'Network error' };
  }
}

async function testAuthService() {
  if (!supabaseUrl || supabaseUrl === 'https://placeholder.supabase.co') {
    return { ok: false, latency: null, error: 'No valid URL configured' };
  }
  const start = Date.now();
  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller?.abort(), 6000);
    const res = await fetch(`${supabaseUrl}/auth/v1/settings`, {
      method: 'GET',
      headers: { apikey: supabaseAnonKey || '', 'Content-Type': 'application/json' },
      signal: controller?.signal,
    });
    clearTimeout(timeout);
    const latency = Date.now() - start;
    const ok = res?.status === 200;
    return { ok, latency, httpStatus: res?.status, error: ok ? null : `HTTP ${res?.status}` };
  } catch (err) {
    const latency = Date.now() - start;
    if (err?.name === 'AbortError') return { ok: false, latency, error: 'Request timed out (>6s)' };
    return { ok: false, latency, error: err?.message || 'Network error' };
  }
}

async function testDatabaseConnectivity() {
  if (!supabaseUrl || supabaseUrl === 'https://placeholder.supabase.co') {
    return { ok: false, latency: null, error: 'No valid URL configured' };
  }
  const start = Date.now();
  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller?.abort(), 6000);
    const res = await fetch(`${supabaseUrl}/rest/v1/?apikey=${supabaseAnonKey}`, {
      method: 'HEAD',
      headers: { apikey: supabaseAnonKey || '', 'Content-Type': 'application/json' },
      signal: controller?.signal,
    });
    clearTimeout(timeout);
    const latency = Date.now() - start;
    const ok = [200, 400, 401, 404]?.includes(res?.status);
    const res2 = await fetch(`${supabaseUrl}/rest/v1/rpc/`, {
      method: 'GET',
      headers: { apikey: supabaseAnonKey || '', 'Content-Type': 'application/json' },
    });
    const dbOk = [200, 400, 401, 404, 405]?.includes(res2?.status);
    return {
      ok: ok && dbOk,
      latency,
      httpStatus: res?.status,
      dbStatus: res2?.status,
      error: (ok && dbOk) ? null : `REST: HTTP ${res?.status}, DB: HTTP ${res2?.status}`,
    };
  } catch (err) {
    const latency = Date.now() - start;
    if (err?.name === 'AbortError') return { ok: false, latency, error: 'Request timed out (>6s)' };
    return { ok: false, latency, error: err?.message || 'Network error' };
  }
}

const StatusDot = ({ status }) => {
  const colors = {
    [STATUS?.CHECKING]: 'bg-amber-400 animate-pulse',
    [STATUS?.OK]: 'bg-emerald-500',
    [STATUS?.ERROR]: 'bg-red-500',
    [STATUS?.WARNING]: 'bg-amber-400',
    [STATUS?.IDLE]: 'bg-gray-300',
  };
  return <span className={`inline-block w-2.5 h-2.5 rounded-full flex-shrink-0 ${colors?.[status] || 'bg-gray-400'}`} />;
};

const TestButton = ({ label, icon, status, loading, onTest, result, labels }) => {
  const statusColors = {
    [STATUS?.OK]: 'text-emerald-600',
    [STATUS?.ERROR]: 'text-red-600',
    [STATUS?.WARNING]: 'text-amber-600',
    [STATUS?.CHECKING]: 'text-amber-500',
    [STATUS?.IDLE]: 'text-muted-foreground',
  };

  return (
    <div className="rounded-lg border border-border bg-muted/20 p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <StatusDot status={status} />
          <span className="text-xs font-semibold text-foreground flex items-center gap-1.5">
            <Icon name={icon} size={12} className="text-muted-foreground" />
            {label}
          </span>
        </div>
        <button
          type="button"
          onClick={onTest}
          disabled={loading}
          className="flex items-center gap-1 text-xs px-2 py-1 rounded bg-primary/10 text-primary hover:bg-primary/20 disabled:opacity-50 transition-colors font-medium"
        >
          <Icon name={loading ? 'Loader' : 'Play'} size={10} className={loading ? 'animate-spin' : ''} />
          {loading ? labels?.testing : labels?.test}
        </button>
      </div>
      {result && (
        <div className="space-y-0.5 mt-1">
          <p className={`text-xs flex items-center gap-1 ${statusColors?.[status]}`}>
            <Icon name={status === STATUS?.OK ? 'CheckCircle' : 'XCircle'} size={11} />
            {status === STATUS?.OK ? labels?.success : labels?.failed}
          </p>
          {result?.latency != null && (
            <p className="text-xs text-muted-foreground">
              {labels?.latency}: <span className="font-mono font-medium">{result?.latency}ms</span>
            </p>
          )}
          {result?.httpStatus != null && (
            <p className="text-xs text-muted-foreground">
              HTTP: <span className="font-mono">{result?.httpStatus}</span>
              {result?.dbStatus != null && <span className="font-mono"> / DB: {result?.dbStatus}</span>}
            </p>
          )}
          {result?.error && (
            <p className="text-xs text-red-500 flex items-start gap-1">
              <Icon name="AlertCircle" size={11} className="mt-0.5 flex-shrink-0" />
              {result?.error}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * SupabaseConnectionStatus
 *
 * Props:
 *  - currentLanguage: 'fr' | 'ar'
 *  - onDiagnosticComplete(isReady: boolean): called after auto-run finishes.
 *    isReady = true  → all critical checks passed, login can proceed.
 *    isReady = false → at least one critical check failed, login should be blocked.
 */
const SupabaseConnectionStatus = ({ currentLanguage = 'fr', onDiagnosticComplete }) => {
  const [open, setOpen] = useState(false);
  const [reachStatus, setReachStatus] = useState(STATUS?.CHECKING);
  const [reachResult, setReachResult] = useState(null);
  const [lastChecked, setLastChecked] = useState(null);
  const [checking, setChecking] = useState(false);

  // Individual test states
  const [restState, setRestState] = useState({ status: STATUS?.IDLE, loading: false, result: null });
  const [authState, setAuthState] = useState({ status: STATUS?.IDLE, loading: false, result: null });
  const [dbState, setDbState] = useState({ status: STATUS?.IDLE, loading: false, result: null });

  // Auto-run state
  const [autoRunDone, setAutoRunDone] = useState(false);
  const [autoRunning, setAutoRunning] = useState(false);

  const { issues, info } = diagnoseConfig();
  const configStatus = issues?.some((i) => i?.type === 'error')
    ? STATUS?.ERROR
    : issues?.some((i) => i?.type === 'warning')
    ? STATUS?.WARNING
    : STATUS?.OK;

  const runCheck = useCallback(async () => {
    setChecking(true);
    setReachStatus(STATUS?.CHECKING);
    const result = await testRestEndpoint();
    setReachResult(result);
    setReachStatus(result?.ok ? STATUS?.OK : STATUS?.ERROR);
    setLastChecked(new Date());
    setChecking(false);
  }, []);

  // Auto-run all diagnostics on mount
  const runAllDiagnostics = useCallback(async () => {
    if (autoRunning) return;
    setAutoRunning(true);
    setRestState({ status: STATUS?.CHECKING, loading: true, result: null });
    setAuthState({ status: STATUS?.CHECKING, loading: true, result: null });
    setDbState({ status: STATUS?.CHECKING, loading: true, result: null });
    setReachStatus(STATUS?.CHECKING);

    const [r1, r2, r3] = await Promise.all([
      testRestEndpoint(),
      testAuthService(),
      testDatabaseConnectivity(),
    ]);

    setRestState({ status: r1?.ok ? STATUS?.OK : STATUS?.ERROR, loading: false, result: r1 });
    setAuthState({ status: r2?.ok ? STATUS?.OK : STATUS?.ERROR, loading: false, result: r2 });
    setDbState({ status: r3?.ok ? STATUS?.OK : STATUS?.ERROR, loading: false, result: r3 });
    setReachResult(r1);
    setReachStatus(r1?.ok ? STATUS?.OK : STATUS?.ERROR);
    setLastChecked(new Date());
    setAutoRunDone(true);
    setAutoRunning(false);

    // Notify parent: ready only if config is OK and all 3 checks pass
    const configOk = configStatus !== STATUS?.ERROR;
    const allPassed = r1?.ok && r2?.ok && r3?.ok;
    onDiagnosticComplete?.(configOk && allPassed);
  }, [autoRunning, configStatus, onDiagnosticComplete]);

  // Run once on mount
  useEffect(() => {
    runAllDiagnostics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (open && !autoRunDone && !autoRunning) runCheck();
  }, [open, autoRunDone, autoRunning, runCheck]);

  const handleTestRest = async () => {
    setRestState({ status: STATUS?.CHECKING, loading: true, result: null });
    const result = await testRestEndpoint();
    setRestState({ status: result?.ok ? STATUS?.OK : STATUS?.ERROR, loading: false, result });
  };

  const handleTestAuth = async () => {
    setAuthState({ status: STATUS?.CHECKING, loading: true, result: null });
    const result = await testAuthService();
    setAuthState({ status: result?.ok ? STATUS?.OK : STATUS?.ERROR, loading: false, result });
  };

  const handleTestDb = async () => {
    setDbState({ status: STATUS?.CHECKING, loading: true, result: null });
    const result = await testDatabaseConnectivity();
    setDbState({ status: result?.ok ? STATUS?.OK : STATUS?.ERROR, loading: false, result });
  };

  const handleTestAll = async () => {
    setRestState({ status: STATUS?.CHECKING, loading: true, result: null });
    setAuthState({ status: STATUS?.CHECKING, loading: true, result: null });
    setDbState({ status: STATUS?.CHECKING, loading: true, result: null });
    const [r1, r2, r3] = await Promise.all([testRestEndpoint(), testAuthService(), testDatabaseConnectivity()]);
    setRestState({ status: r1?.ok ? STATUS?.OK : STATUS?.ERROR, loading: false, result: r1 });
    setAuthState({ status: r2?.ok ? STATUS?.OK : STATUS?.ERROR, loading: false, result: r2 });
    setDbState({ status: r3?.ok ? STATUS?.OK : STATUS?.ERROR, loading: false, result: r3 });
    setLastChecked(new Date());

    const configOk = configStatus !== STATUS?.ERROR;
    const allPassed = r1?.ok && r2?.ok && r3?.ok;
    onDiagnosticComplete?.(configOk && allPassed);
  };

  const overallStatus =
    configStatus === STATUS?.ERROR
      ? STATUS?.ERROR
      : reachStatus === STATUS?.ERROR
      ? STATUS?.ERROR
      : configStatus === STATUS?.WARNING || reachStatus === STATUS?.WARNING
      ? STATUS?.WARNING
      : reachStatus === STATUS?.CHECKING
      ? STATUS?.CHECKING
      : STATUS?.OK;

  // Derive whether login should be blocked
  const hasCriticalFailure =
    autoRunDone &&
    (configStatus === STATUS?.ERROR ||
      restState?.status === STATUS?.ERROR ||
      authState?.status === STATUS?.ERROR);

  const labels = {
    fr: {
      title: 'Diagnostic Supabase',
      config: 'Configuration',
      connection: 'Connexion',
      reachable: 'Serveur accessible',
      unreachable: 'Serveur inaccessible',
      latency: 'Latence',
      retry: 'Relancer',
      checking: 'Vérification…',
      lastChecked: 'Vérifié à',
      noIssues: 'Configuration correcte',
      httpStatus: 'Statut HTTP',
      tests: 'Tests de connectivité',
      testAll: 'Tout tester',
      test: 'Tester',
      testing: 'Test…',
      success: 'Succès',
      failed: 'Échec',
      restLabel: 'REST Endpoint',
      authLabel: 'Service Auth',
      dbLabel: 'Base de données',
      autoRunning: 'Vérification de la connexion Supabase…',
      criticalError: 'Connexion Supabase impossible. Vérifiez l\'URL et la clé anon avant de vous connecter.',
      allOk: 'Connexion Supabase vérifiée — prêt à se connecter.',
      retryAll: 'Relancer le diagnostic',
    },
    ar: {
      title: 'تشخيص Supabase',
      config: 'الإعداد',
      connection: 'الاتصال',
      reachable: 'الخادم متاح',
      unreachable: 'الخادم غير متاح',
      latency: 'زمن الاستجابة',
      retry: 'إعادة المحاولة',
      checking: 'جارٍ الفحص…',
      lastChecked: 'آخر فحص',
      noIssues: 'الإعداد صحيح',
      httpStatus: 'حالة HTTP',
      tests: 'اختبارات الاتصال',
      testAll: 'اختبار الكل',
      test: 'اختبار',
      testing: 'جارٍ…',
      success: 'نجاح',
      failed: 'فشل',
      restLabel: 'REST Endpoint',
      authLabel: 'خدمة المصادقة',
      dbLabel: 'قاعدة البيانات',
      autoRunning: 'جارٍ التحقق من اتصال Supabase…',
      criticalError: 'تعذّر الاتصال بـ Supabase. تحقق من URL والمفتاح قبل تسجيل الدخول.',
      allOk: 'تم التحقق من اتصال Supabase — جاهز لتسجيل الدخول.',
      retryAll: 'إعادة التشخيص',
    },
  };
  const l = labels?.[currentLanguage] || labels?.fr;

  const statusLabel = {
    [STATUS?.OK]: { text: 'OK', color: 'text-emerald-600' },
    [STATUS?.ERROR]: { text: 'Erreur', color: 'text-red-600' },
    [STATUS?.WARNING]: { text: 'Avertissement', color: 'text-amber-600' },
    [STATUS?.CHECKING]: { text: l?.checking, color: 'text-amber-600' },
  };

  return (
    <div className="mt-4">
      {/* Auto-run status banner */}
      {autoRunning && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-700 text-xs mb-2">
          <Icon name="Loader" size={13} className="animate-spin flex-shrink-0" />
          <span>{l?.autoRunning}</span>
        </div>
      )}

      {/* Critical failure banner — shown persistently when checks fail */}
      {!autoRunning && hasCriticalFailure && (
        <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-red-50 border border-red-200 text-red-700 text-xs mb-2">
          <Icon name="AlertTriangle" size={13} className="flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-semibold mb-0.5">{l?.criticalError}</p>
          </div>
          <button
            type="button"
            onClick={runAllDiagnostics}
            className="flex items-center gap-1 text-xs px-2 py-1 rounded bg-red-100 hover:bg-red-200 text-red-700 font-medium transition-colors flex-shrink-0"
          >
            <Icon name="RefreshCw" size={10} />
            {l?.retryAll}
          </button>
        </div>
      )}

      {/* All-OK banner */}
      {!autoRunning && autoRunDone && !hasCriticalFailure && overallStatus === STATUS?.OK && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs mb-2">
          <Icon name="CheckCircle" size={13} className="flex-shrink-0" />
          <span>{l?.allOk}</span>
        </div>
      )}

      {/* Collapsible detail panel toggle */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-border bg-muted/40 hover:bg-muted/70 transition-colors text-sm"
      >
        <span className="flex items-center gap-2 font-medium text-muted-foreground">
          <StatusDot status={overallStatus} />
          {l?.title}
        </span>
        <span className={`text-xs font-semibold ${statusLabel?.[overallStatus]?.color}`}>
          {statusLabel?.[overallStatus]?.text}
        </span>
        <Icon name={open ? 'ChevronUp' : 'ChevronDown'} size={14} className="text-muted-foreground ml-auto" />
      </button>

      {open && (
        <div className="mt-2 rounded-lg border border-border bg-card shadow-sm overflow-hidden text-sm">
          {/* Config Section */}
          <div className="px-4 py-3 border-b border-border">
            <div className="flex items-center gap-2 mb-2">
              <StatusDot status={configStatus} />
              <span className="font-semibold text-foreground">{l?.config}</span>
            </div>
            {issues?.length === 0 ? (
              <p className="text-xs text-emerald-600 flex items-center gap-1">
                <Icon name="CheckCircle" size={12} /> {l?.noIssues}
              </p>
            ) : (
              <ul className="space-y-1">
                {issues?.map((issue, i) => (
                  <li key={i} className={`flex items-start gap-1.5 text-xs ${issue?.type === 'error' ? 'text-red-600' : 'text-amber-600'}`}>
                    <Icon name={issue?.type === 'error' ? 'XCircle' : 'AlertTriangle'} size={12} className="mt-0.5 flex-shrink-0" />
                    {issue?.msg}
                  </li>
                ))}
              </ul>
            )}
            {info?.length > 0 && (
              <div className="mt-2 space-y-1">
                {info?.map((item, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span className="font-medium text-foreground/70">{item?.label}:</span>
                    <span className="font-mono truncate max-w-[200px]">{item?.value}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Reachability Section */}
          <div className="px-4 py-3 border-b border-border">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <StatusDot status={reachStatus} />
                <span className="font-semibold text-foreground">{l?.connection}</span>
              </div>
              <button
                type="button"
                onClick={runCheck}
                disabled={checking}
                className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 disabled:opacity-50 transition-colors"
              >
                <Icon name="RefreshCw" size={11} className={checking ? 'animate-spin' : ''} />
                {l?.retry}
              </button>
            </div>

            {reachStatus === STATUS?.CHECKING ? (
              <p className="text-xs text-amber-600 flex items-center gap-1">
                <Icon name="Loader" size={12} className="animate-spin" /> {l?.checking}
              </p>
            ) : reachResult ? (
              <div className="space-y-1">
                <p className={`text-xs flex items-center gap-1 ${reachResult?.ok ? 'text-emerald-600' : 'text-red-600'}`}>
                  <Icon name={reachResult?.ok ? 'CheckCircle' : 'XCircle'} size={12} />
                  {reachResult?.ok ? l?.reachable : l?.unreachable}
                </p>
                {reachResult?.latency != null && (
                  <p className="text-xs text-muted-foreground">
                    {l?.latency}: <span className="font-mono font-medium">{reachResult?.latency}ms</span>
                  </p>
                )}
                {reachResult?.httpStatus != null && (
                  <p className="text-xs text-muted-foreground">
                    {l?.httpStatus}: <span className="font-mono">{reachResult?.httpStatus}</span>
                  </p>
                )}
                {reachResult?.error && (
                  <p className="text-xs text-red-500 flex items-start gap-1">
                    <Icon name="AlertCircle" size={12} className="mt-0.5 flex-shrink-0" />
                    {reachResult?.error}
                  </p>
                )}
              </div>
            ) : null}

            {lastChecked && (
              <p className="text-xs text-muted-foreground mt-2">
                {l?.lastChecked}: {lastChecked?.toLocaleTimeString()}
              </p>
            )}
          </div>

          {/* Interactive Test Buttons Section */}
          <div className="px-4 py-3">
            <div className="flex items-center justify-between mb-3">
              <span className="font-semibold text-foreground text-xs uppercase tracking-wide">{l?.tests}</span>
              <button
                type="button"
                onClick={handleTestAll}
                disabled={restState?.loading || authState?.loading || dbState?.loading}
                className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors font-medium"
              >
                <Icon name="Zap" size={11} />
                {l?.testAll}
              </button>
            </div>
            <div className="space-y-2">
              <TestButton
                label={l?.restLabel}
                icon="Globe"
                status={restState?.status}
                loading={restState?.loading}
                result={restState?.result}
                onTest={handleTestRest}
                labels={l}
              />
              <TestButton
                label={l?.authLabel}
                icon="Shield"
                status={authState?.status}
                loading={authState?.loading}
                result={authState?.result}
                onTest={handleTestAuth}
                labels={l}
              />
              <TestButton
                label={l?.dbLabel}
                icon="Database"
                status={dbState?.status}
                loading={dbState?.loading}
                result={dbState?.result}
                onTest={handleTestDb}
                labels={l}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupabaseConnectionStatus;
